import { type ReactNode } from 'react';

export interface CardProps {
  readonly children: ReactNode;
  readonly title?: string;
  readonly subtitle?: string;
  /** Right-aligned slot in the header row (e.g. an action button). */
  readonly headerRight?: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
  /** Skip body padding — useful when nesting a DataTable that paints its own gutters. */
  readonly noPadding?: boolean;
}

/**
 * Card container with optional header/body/footer slots. Mirrors HTHD's
 * admin `Card.tsx` prop shape (`title`, `subtitle`, `headerRight`,
 * `noPadding`) plus an optional `footer` slot for rich card layouts.
 */
export function Card({
  children,
  title,
  subtitle,
  headerRight,
  footer,
  className = '',
  noPadding = false,
}: CardProps): JSX.Element {
  const hasHeader = Boolean(title || subtitle || headerRight);
  const cls = `ferro-ui-card${className ? ` ${className}` : ''}`;
  const bodyCls = `ferro-ui-card__body${noPadding ? ' ferro-ui-card__body--flush' : ''}`;

  return (
    <section className={cls}>
      {hasHeader ? (
        <header className="ferro-ui-card__header">
          <div className="ferro-ui-card__heading">
            {title ? <h2 className="ferro-ui-card__title">{title}</h2> : null}
            {subtitle ? <p className="ferro-ui-card__subtitle">{subtitle}</p> : null}
          </div>
          {headerRight ? <div className="ferro-ui-card__actions">{headerRight}</div> : null}
        </header>
      ) : null}
      <div className={bodyCls}>{children}</div>
      {footer ? <footer className="ferro-ui-card__footer">{footer}</footer> : null}
    </section>
  );
}
